import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const supabaseConfigured = true;

export type AppRole = "owner" | "admin" | "user";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  configured: boolean;
  role: AppRole | null;
  telegramId: number | null;
  roleLoading: boolean;
  isStaff: boolean;
  isOwner: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AppRole | null>(null);
  const [telegramId, setTelegramId] = useState<number | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const currentUserId = useRef<string | null>(null);
  const currentToken = useRef<string | null>(null);

  // Role + Telegram-link status come from the same-origin /api/me function, which
  // reads with the service role. We do NOT query `user_roles` directly from the
  // browser: its live SELECT RLS policy self-references `user_roles` and throws
  // 42P17 "infinite recursion", which used to fail this call for every user and
  // strand them on /activate. /api/me also bridges `telegram_identity_links`
  // (where the bot writes the canonical link), so a link made in either place is
  // detected here.
  const loadRole = useCallback(
    async (userId: string | null, accessToken?: string | null) => {
      if (!userId) {
        setRole(null);
        setTelegramId(null);
        setRoleLoading(false);
        return;
      }
      const token = accessToken ?? currentToken.current;
      setRoleLoading(true);
      try {
        const res = await fetch("/api/me", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = (await res.json()) as {
            role?: string;
            telegramId?: number | null;
          };
          setRole((data.role as AppRole) ?? "user");
          setTelegramId(data.telegramId != null ? Number(data.telegramId) : null);
        } else {
          setRole("user");
          setTelegramId(null);
        }
      } catch {
        setRole("user");
        setTelegramId(null);
      }
      setRoleLoading(false);
    },
    [],
  );

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      const newUid = newSession?.user.id ?? null;
      currentToken.current = newSession?.access_token ?? null;
      if (newUid !== currentUserId.current || event === "USER_UPDATED") {
        currentUserId.current = newUid;
        void loadRole(newUid, newSession?.access_token);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      const uid = data.session?.user.id ?? null;
      currentUserId.current = uid;
      currentToken.current = data.session?.access_token ?? null;
      void loadRole(uid, data.session?.access_token);
      setLoading(false);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [loadRole]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setRole(null);
    setTelegramId(null);
    currentUserId.current = null;
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
  }, []);

  const refreshRole = useCallback(async () => {
    await loadRole(currentUserId.current);
  }, [loadRole]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: supabaseConfigured,
      role,
      telegramId,
      roleLoading,
      isStaff: role === "owner" || role === "admin",
      isOwner: role === "owner",
      signOut,
      refresh,
      refreshRole,
    }),
    [session, loading, role, telegramId, roleLoading, signOut, refresh, refreshRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
